<script lang="ts">
  import { Skeleton } from "@/lib/components/ui/skeleton";
  import { convertDataToXSSTargetsResponse, overrideXSSTargets, resetUserOverriddenXSSTargets, type XSSTargetsResponse } from "@/Util/api";
  import DayAvailabilityPopover from "../Calendar/DayAvailabilityPopover.svelte";
  import { getContext } from "svelte";
  import type { MyFitnessStores } from "../stores";
  import Button from "@/lib/components/ui/button/button.svelte";
  import * as Dialog from '$lib/components/ui/dialog';
  import XssInput from "./XssInput.svelte";

  export let xssTargetsResponse: XSSTargetsResponse;
  export let isLoading: boolean = false;
  export let hideActions: boolean = false;

  const { trainingAdvice, trainingPlan } = getContext<MyFitnessStores>('myFitness');

  let xssTargetsDialogOpen = false;
  let xlss = 0;
  let xhss = 0;
  let xpss = 0;

  async function overrideXSSTargetsWrapper(xlss?: number, xhss?: number, xpss?: number) {
    const lxss = +(xlss ?? 0);
    const hxss = +(xhss ?? 0);
    const pxss = +(xpss ?? 0);
    const xssNum = lxss + hxss + pxss;
    if (lxss < 0 || hxss < 0 || pxss < 0 || isNaN(lxss) || isNaN(hxss) || isNaN(pxss)) {
      alert('Please enter valid non-negative numbers for all targets.');
      return;
    }
    isLoading = true;
    xssTargetsResponse = await overrideXSSTargets({
      xss: xssNum,
      xlss: xlss,
      xhss: xhss,
      xpss: xpss,
    });
    isLoading = false;

    xssTargetsDialogOpen = false;
  }

  async function resetXSSTargets() {
    // If the XSSTargets haven't been overridden yet, then no need to call the API
    // Just reset the variables to match the targets
    if (xssTargetsResponse?.source === 'Override') {
      isLoading = true;
      xssTargetsResponse = await resetUserOverriddenXSSTargets();
      isLoading = false;
    } else {
      xssTargetsResponse = xssTargetsResponse; // trigger reactivity
    }

    xssTargetsDialogOpen = false;
  }

</script>

<div class='flex flex-col'>
  <h2 class="m-0 text-lg font-semibold">XSS Overview</h2>
  {#if !xssTargetsResponse}
    <div class="flex flex-col gap-1">
      <Skeleton class="w-full h-[14px]" />
      <Skeleton class="w-full h-[14px]" />
      <Skeleton class="w-full h-[14px]" />
      <Skeleton class="w-full h-[14px]" />
    </div>
  {:else}
    <!-- Compact 5x4 grid: (Label | Low | High | Peak | Total) -->
    <div class="inline-grid text-xs font-medium mb-2" style="grid-template-columns: auto repeat(4, auto); gap:2px 12px;">
      <!-- Legend Row -->
      <div></div>
      <div class="flex items-center justify-end gap-1 text-gray-500"><span class="w-2 h-2 rounded-full bg-black"></span><span>Total</span></div>
      <div class="flex items-center justify-end gap-1 text-gray-500"><span class="w-2 h-2 rounded-full" style="background:var(--color-xlss)"></span><span>Low</span></div>
      <div class="flex items-center justify-end gap-1 text-gray-500"><span class="w-2 h-2 rounded-full" style="background:var(--color-xhss)"></span><span>High</span></div>
      <div class="flex items-center justify-end gap-1 text-gray-500"><span class="w-2 h-2 rounded-full" style="background:var(--color-xpss)"></span><span>Peak</span></div>

      <!-- Original Targets Row -->
      <div class="pr-2 whitespace-nowrap text-gray-600 w-max relative">
        Daily Targets
        {#if xssTargetsResponse.source === 'Override'}
          <div class='h-1.5 w-1.5 rounded-full bg-red-500 absolute top-0 right-0' />
        {/if}
      </div>
      <div class="text-right tabular-nums text-black">{xssTargetsResponse.originalTargets.xss}</div>
      <div class="text-right tabular-nums" style="color:var(--color-xlss)">{xssTargetsResponse.originalTargets.xlss}</div>
      <div class="text-right tabular-nums" style="color:var(--color-xhss)">{xssTargetsResponse.originalTargets.xhss}</div>
      <div class="text-right tabular-nums" style="color:var(--color-xpss)">{xssTargetsResponse.originalTargets.xpss}</div>

      <!-- Completed Row -->
      <div class="pr-2 whitespace-nowrap text-gray-600">Completed</div>
      <div class="text-right tabular-nums text-black">{xssTargetsResponse.completed.xss}</div>
      <div class="text-right tabular-nums" style="color:var(--color-xlss)">{xssTargetsResponse.completed.xlss}</div>
      <div class="text-right tabular-nums" style="color:var(--color-xhss)">{xssTargetsResponse.completed.xhss}</div>
      <div class="text-right tabular-nums" style="color:var(--color-xpss)">{xssTargetsResponse.completed.xpss}</div>

      <!-- Remaining Row -->
      <div class="pr-2 whitespace-nowrap text-gray-600 w-max relative">
        Remaining Targets
        {#if xssTargetsResponse.source === 'FF_current'}
          <div class='h-1.5 w-1.5 rounded-full bg-red-500 absolute top-0 right-0' />
        {/if}
      </div>
      {#if xssTargetsResponse.remaining}
        <div class="text-right tabular-nums text-black">{xssTargetsResponse.remaining.xss}</div>
        <div class="text-right tabular-nums" style="color:var(--color-xlss)">{xssTargetsResponse.remaining.xlss}</div>
        <div class="text-right tabular-nums" style="color:var(--color-xhss)">{xssTargetsResponse.remaining.xhss}</div>
        <div class="text-right tabular-nums" style="color:var(--color-xpss)">{xssTargetsResponse.remaining.xpss}</div>
      {:else}
        <div class="text-right text-gray-400">-</div>
        <div class="text-right text-gray-400">-</div>
        <div class="text-right text-gray-400">-</div>
        <div class="text-right text-gray-400">-</div>
      {/if}
    </div>

    {#if xssTargetsResponse.source === 'Override'}
      <div class='text-muted-foreground text-sm mb-2'>
        Daily targets have been manually overridden.
      </div>
    {:else if xssTargetsResponse.source === 'FF_historical'}
      <div class='text-muted-foreground text-sm mb-2'>
        Daily targets are based on your freshness feedback from earlier today.
      </div>
    {:else if xssTargetsResponse.source === 'FF_current'}
      {#if +xssTargetsResponse.completed.xss > 0}
        <div class='text-muted-foreground text-sm mb-2'>
          Remaining targets are based on your current freshness feedback. This adjusts your daily targets accordingly.
        </div>
      {:else}
        <div class='text-muted-foreground text-sm mb-2'>
          Daily targets are based on your current freshness feedback.
        </div>
      {/if}
    {:else if $trainingAdvice.availability === 0}
      <div class='text-muted-foreground text-sm mb-2'>
        Unavailable for training today.
      </div>
    {:else if !$trainingPlan.isForecastPlan && +xssTargetsResponse.completed.xss === 0}
      <div class='text-muted-foreground text-sm mb-2'>
        Daily targets are based on an
        availability of <strong>{Math.round($trainingAdvice.availability*10)/10} 
          {#if Math.round($trainingAdvice.availability*10)/10 == 1} 
          hour 
          {:else} 
          hours
          {/if}
      </div>
    {/if}

    {#if !hideActions}
      <div class='flex flex-row gap-2 items-center'>
        {#if !$trainingPlan.isForecastPlan && +xssTargetsResponse.completed.xss === 0}
          <DayAvailabilityPopover date={new Date()} showScope asModal onSaved={(data) => {
            // Update targets based on the new advice
            if (data?.xss_targets) xssTargetsResponse = convertDataToXSSTargetsResponse(data.xss_targets);
          }} let:builder>
            <Button builders={[builder]} variant='outline' size='sm'>
              <i class='fa-thin fa-clock mr-1' /> Adjust Training Availability
            </Button>
          </DayAvailabilityPopover>
        {/if}
        <Button variant="outline" size='sm' on:click={() => (xssTargetsDialogOpen = true)}>Edit Target XSS</Button>
      </div>
    {/if}
    
    <Dialog.Root bind:open={xssTargetsDialogOpen} portal="#shadcn-ui-dialog-target">
      <Dialog.Content class="flex flex-col max-h-[95dvh] md:h-[400px] max-w-[600px] w-[95vw] p-0 overflow-hidden rounded-xl">
        <div class="flex-1 overflow-y-auto px-5 pb-5 pt-4 space-y-6">
          <XssInput xssDetails={xssTargetsResponse?.remainingTargetsNumeric} bind:xlss bind:xhss bind:xpss />
        </div>

        <!-- Footer actions -->
        <div class="flex flex-row flex-wrap w-full justify-end gap-3 px-5 py-4 border-t border-border bg-muted/30">
          <Button variant="outline" on:click={() => (xssTargetsDialogOpen = false)}>Cancel</Button>
          <Button variant="outline" disabled={isLoading || (xssTargetsResponse?.remainingTargetsNumeric?.xlss == xlss && xssTargetsResponse?.remainingTargetsNumeric?.xhss == xhss && xssTargetsResponse?.remainingTargetsNumeric?.xpss == xpss && xssTargetsResponse?.source !== 'Override')} on:click={resetXSSTargets}>Reset</Button>
          <Button variant="outline" disabled={isLoading || (xssTargetsResponse?.remainingTargetsNumeric?.xlss == xlss && xssTargetsResponse?.remainingTargetsNumeric?.xhss == xhss && xssTargetsResponse?.remainingTargetsNumeric?.xpss == xpss)} on:click={() => overrideXSSTargetsWrapper(xlss, xhss, xpss)}>Override</Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  {/if}
</div>
